import { unparse } from 'papaparse'
import { exactDecimalSum } from './exportClientTotals'
import { toNumber } from './reportGridUtils'
import { singleLine } from './exportCell'
import { reAddMatchesServer } from './groupedExportView'

// ExcelJS is heavy and pulls Node-ish modules; load it lazily (only when the user
// actually exports an .xlsx) so importing this module for buildExportMatrix /
// CSV — and every test that transitively imports it — never has to resolve it,
// and Vite can code-split it out of the main bundle.

/**
 * Client-side "export what you see" (CC-49370). Builds a CSV / Excel file from a
 * grid's current view instead of the server's base report, honoring the user's
 * scope choices (visible columns & order, and/or active filters + current sort).
 *
 * A `view` is published by the active report grid:
 *   {
 *     columns:     [{ key, label, visible, order, numeric, summable }], // every column
 *     visibleRows: [{ [key]: value }],   // filtered + SORTED, display order
 *     allRows:     [{ [key]: value }],   // every row, base (server) order
 *   }
 * `summable` marks columns the totals row may safely add (the SUM-only guardrail,
 * CC-49382) — averages/rates/weighted columns are excluded so the export never
 * shows a fabricated total.
 * Cell values arrive as EXPORT TEXT, already converted exactly once by the view
 * builder (`exportCell` / `exportText` / `groupHeadingText`: tags removed, then
 * entities decoded); numeric columns carry real numbers so the export stays
 * summable in Excel (not text). Excel is written with ExcelJS (actively
 * maintained, no known criticals) — replacing the unmaintained, vulnerable
 * SheetJS/xlsx fork.
 *
 * Spec 191: this module therefore writes text values VERBATIM and never strips
 * HTML again. It used to: CC-49370's first views carried raw display HTML and the
 * strip lived here, then spec 133 moved the conversion into every view builder and
 * the strip here became a second pass. A second pass reads the DECODED text as
 * markup, so a firm's literal `Client Three & Sons <Ltd>` (escaped on screen as
 * `&lt;Ltd&gt;`) lost `<Ltd>` in every CSV, XLSX and PDF, silently. A new view
 * producer must hand over text the way the three builders do, not HTML.
 */

/**
 * One exported cell: a real number for a numeric column (a formatted string is
 * coerced back; a value that is not a number stays the screen's own text, e.g.
 * "—"), otherwise the view's text as it is. Shared by the flat matrix and the PDF's
 * grouped body (spec 127 §4.6) so both convert a value the same way. Never strips
 * HTML: the value is already export text (spec 191, module note).
 */
export const exportCellValue = (v, c) => {
  if (c?.numeric) {
    if (typeof v === 'number') return v
    if (v == null || v === '') return null
    const n = toNumber(v)
    return n != null ? n : String(v)
  }
  return v == null ? '' : String(v)
}

/** Apply the scope flags to a view → { headers, matrix, cols }. Values keep type:
 * a real number for numeric columns, otherwise the view's text verbatim. */
export const buildExportMatrix = (view, { honorColumns = false, honorFilters = false, respectCollapse = false } = {}) => {
  if (!view || !view.columns) return { headers: [], matrix: [], cols: [] }
  let cols = view.columns
  if (honorColumns) {
    // Honor visible columns, in the user's current order.
    cols = cols.filter((c) => c.visible).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
  } else if (honorFilters) {
    // "What you see" — drop user-hidden columns even when only filters are honored
    // (keep source order). Fixes the nit where honor-filters-only leaked hidden cols.
    cols = cols.filter((c) => c.visible)
  }
  // Honor filters → the filtered + sorted rows the user sees; otherwise all rows.
  // CC-49843: `respectCollapse` (the PDF/print path) swaps in the collapse-aware
  // sets a grouped grid publishes, so a collapsed section prints as its header +
  // subtotal instead of every leaf. Grids that publish no printRows (the flat
  // grid has no groups to collapse) fall through to the originals unchanged.
  const collapseAware = respectCollapse
    ? (honorFilters ? view.printRows : view.printRowsAll)
    : null
  const rows = collapseAware || (honorFilters ? (view.visibleRows || []) : (view.allRows || []))
  const headers = cols.map((c) => c.label)
  const matrix = rows.map((r) => cols.map((c) => exportCellValue(r[c.key], c)))
  return { headers, matrix, cols }
}

/**
 * May the export re-add a summable column's rows as the GROUPED grid's total? The
 * grouped grid's own footer decides per scope (`footerRecompute`,
 * groupedExportView.js): a column is re-added only when ALL of the scope's leaves
 * add up to CB's figure; otherwise CB's figure stands, filtered or not. The export
 * used to skip that check and trust the declared SUM alone — but a column can be
 * declared SUM while the rows CB sends are not the rows its total covers. Measured
 * on Fees Billed By Timekeeper (report 50, `grid-generic-50.json`, CB's own run):
 * one leaf per timekeeper, 8,663 in all, under CB's 165,458 — so every client
 * export printed "Total 8,663", and the grouped PDF printed it directly under CB's
 * per-timekeeper subtotals, which sum to 165,458 (review of spec 127). The same
 * inputs as the screen: every leaf (`view.allRows`) against CB's displayed total,
 * the same tolerance.
 *
 * Only a view the GROUPED grid published (it carries `outline`) takes the rule —
 * the flat grid's footer re-adds the visible rows without a per-scope check, and
 * its export keeps matching it. No view, or no CB figure to compare against: the
 * re-sum is kept exactly as before.
 */
const reSumAgreesWithServer = (c, view) => {
  if (!Array.isArray(view?.outline) || !Array.isArray(view?.allRows)) return true
  if (String(c?.serverTotal ?? '').trim() === '') return true // no CB figure at all
  const serverNum = toNumber(c.serverTotal)
  // A CB figure the screen cannot read as a number ("($1,234.00)") is never
  // re-added on screen either (footerRecompute needs a number): CB's stands.
  if (serverNum == null) return false
  let all = 0
  for (const r of view.allRows) {
    const v = exportCellValue(r?.[c.key], c)
    if (typeof v === 'number' && Number.isFinite(v)) all += v
  }
  // CC-54262: the screen's own comparison, imported, never re-typed — a CB figure
  // displayed with fewer decimals than its leaves carry is still a plain sum.
  return reAddMatchesServer(all, c.serverTotal)
}

/**
 * The re-add the grids' own footers do: doubles, left to right from 0, as
 * ReportDataGrid's `footerValue` and the grouped grid's `footerRecompute` add the
 * same rows. Null when there is nothing to add.
 */
const screenSum = (values) => {
  let sum = 0
  let any = false
  for (const v of values) {
    if (typeof v === 'number' && Number.isFinite(v)) { sum += v; any = true }
  }
  return any ? sum : null
}

/**
 * buildTotalsRow's row PLUS the index of the synthesized "Total" label it wrote
 * (-1 when it wrote none), so buildFooterRows can tell CB's text from ours.
 * Returns null when no column can carry a value at all. `view` (optional) lets a
 * summable column be re-added only where that agrees with CB (above).
 *
 * Spec 179 (CC-54134): `exact` decides only HOW a re-added column is added, never
 * WHICH columns are. A file takes the exact decimal sum (`exactDecimalSum`); the
 * double sum the screen computes printed "619989.7999999999" into every client CSV
 * total that happened to land off a cent. `exact: false` keeps the screen's double
 * for the one consumer that must quote the screen digit for digit (the AI payload,
 * spec 153 — see buildFooterRowsWithSource).
 */
const totalsRowParts = (cols, matrix, view, exact = false) => {
  const summableIdx = cols.map((c, i) => (c?.summable && reSumAgreesWithServer(c, view) ? i : -1)).filter((i) => i >= 0)
  const hasServerTotal = (c) => c?.serverTotal != null && String(c.serverTotal).trim() !== ''
  if (summableIdx.length === 0 && !cols.some(hasServerTotal)) return null
  const row = cols.map(() => '')
  for (const i of summableIdx) {
    const column = matrix.map((m) => m[i])
    const sum = exact ? exactDecimalSum(column) : screenSum(column)
    row[i] = sum == null ? '' : sum
  }
  // Server-total fallback for anything still blank (CC-49872).
  cols.forEach((c, i) => {
    if (row[i] === '' && hasServerTotal(c)) row[i] = String(c.serverTotal)
  })
  // Label goes in the first column with no value so it never overwrites one.
  let labelIdx = cols.findIndex((c, i) => row[i] === '' && !c?.summable)
  if (labelIdx === -1) labelIdx = row.findIndex((v) => v === '')
  if (labelIdx !== -1 && row[labelIdx] === '') {
    row[labelIdx] = 'Total'
    return { row, labelIdx }
  }
  return { row, labelIdx: -1 }
}

/**
 * Build a totals row aligned to `cols` for the exported `matrix` (CC-49457).
 * Sums only `summable` columns (the SUM-only guardrail), over the rows actually
 * being exported — so a filtered export totals the filtered rows.
 *
 * CC-49872: a totals column the guardrail refuses to re-sum — or whose exported
 * cells didn't land as numbers — falls back to the SERVER total the grid
 * publishes per column (`serverTotal`), mirroring the on-screen grid, which
 * keeps the server value rather than showing a "Total" label with nothing under
 * it. Non-summable columns without a server total stay blank (we never
 * fabricate an average/rate total). The word "Total" labels the first column
 * that carries neither. Returns null when no column can carry a value at all.
 * A re-added total is the exact decimal sum of the exported cells (spec 179).
 */
export const buildTotalsRow = (cols, matrix) => totalsRowParts(cols, matrix, undefined, true)?.row ?? null

/**
 * The footer BLOCK for an export, aligned to `cols` — an array of rows, not one row
 * (CC-49860).
 *
 * A report footer is not always a single line. Timekeeper Analysis closes with two
 * ("Total / PTD" and "Total / YTD"); a balance sheet closes with "Grand Total:
 * Assets" and "Grand Total: Liabilities & Equity". The grids have rendered those
 * correctly since CC-49860, but every export re-derived ONE row from the
 * per-column `serverTotal` scalars, so the second line silently vanished — and the
 * label it printed was the synthesized word "Total" rather than CB's own
 * "Total / YTD". That was reported as a PDF bug; it affected the client CSV and
 * Excel paths identically.
 *
 * When the grid publishes `view.footerRows`, those rows are emitted VERBATIM in CB's
 * order — no re-summing, which matters because only row 0 is validated against the
 * SUM-only guardrail; rows 1..N are server values we must never recompute. Absent
 * that, this falls back to the single derived totals row so existing consumers keep
 * their current behavior.
 *
 * Spec 179 (CC-54134): this is the FILES' footer — the client CSV, XLSX and PDF all
 * write it — so a re-added total is the exact decimal sum of the exported cells,
 * never the double sum that wrote "398261.95999999996" under CB's 398,261.96.
 */
export const buildFooterRows = (cols, matrix, view) => buildFooterRowsWithSource(cols, matrix, view, { exact: true }).rows

/**
 * buildFooterRows, plus WHERE each figure of the first footer row came from (spec
 * 153), so a consumer that has to say what a total covers can say it truthfully:
 *   'rows'   re-added over the exported rows (a SUM-only column whose rows agree
 *            with CB) — it covers exactly the rows being exported;
 *   'server' CB's own footer value, kept as CB sent it — it covers the WHOLE
 *            report, whatever filter the exported rows went through;
 *   null     nothing there (a blank cell, or the synthesized "Total" label).
 * Rows 1..N are always CB's verbatim (see below), so only row 0 needs saying.
 *
 * With `{ exact: true }`, `rows` is exactly what buildFooterRows returns — this
 * reports a decision the function already makes and decides nothing itself. Only
 * a re-added column ever holds a NUMBER in the derived row: the server fallback
 * writes CB's string and the label is ours.
 *
 * Spec 179: without `exact` (the default, and the AI payload's call), a re-added
 * figure is the DOUBLE sum the grids' footers compute — same rows, same order, same
 * float — so the AI can quote a re-add through the footer's own formatter and land
 * on the footer's digit (spec 153: 190 filtered to "c" re-adds to 325.34999999999997,
 * which the footer prints "325.3"). The two differ only by that float noise; which
 * columns are re-added, and every source, are identical either way.
 */
export const buildFooterRowsWithSource = (cols, matrix, view, { exact = false } = {}) => {
  const published = (Array.isArray(view?.footerRows) ? view.footerRows : [])
    .map((r) => cols.map((c) => {
      const v = r?.[c.key]
      // Already export text (the builders' footerRows go through exportCell too):
      // verbatim, never stripped a second time (spec 191).
      return v == null ? '' : (typeof v === 'number' ? v : String(v))
    }))
    // A row carrying nothing for the exported column set is noise.
    .filter((row) => row.some((v) => v !== '' && v != null))
  const parts = totalsRowParts(cols, matrix, view, exact)
  const derived = parts?.row ?? null
  const isBlank = (v) => v === '' || v == null
  const derivedSource = (i) => {
    if (isBlank(derived[i]) || i === parts.labelIdx) return null
    return typeof derived[i] === 'number' ? 'rows' : 'server'
  }
  if (published.length === 0) {
    return derived ? { rows: [derived], sources: cols.map((_, i) => derivedSource(i)) } : { rows: [], sources: [] }
  }

  // Row 0 keeps its existing semantics so nothing regresses: a filtered export
  // still re-sums the rows it actually contains (CC-49457) and still falls back to
  // the server total for columns the guardrail refuses to add (CC-49872). What the
  // published row adds is CB's own footer TEXT — "Total", "PTD" — which the derived
  // row cannot know and used to replace with a bare synthesized "Total".
  //
  // Spec 127 §4.6: the synthesized "Total" is added ONLY when CB's first footer
  // row has no text cell at all. A row that carries CB's own label ("Grand Total:
  // Liabilities & Equity", "Net Income") keeps it alone — the synthesized word
  // otherwise landed in the first empty text column, which on the statements is
  // Account Number (verifier 5, C13: "Total | Grand Total: Assets | $…").
  const head = published[0].slice()
  // What CB published is CB's: every non-blank cell of it starts as 'server'.
  const sources = head.map((v) => (isBlank(v) ? null : 'server'))
  const hasText = head.some((v, i) => !cols[i]?.numeric && v !== '' && v != null)
  if (derived) {
    cols.forEach((c, i) => {
      if (c?.summable && typeof derived[i] === 'number') {
        head[i] = derived[i]
        sources[i] = 'rows'
        return
      }
      if (i === parts.labelIdx && hasText) return // CB's own label stands
      if ((head[i] === '' || head[i] == null) && derived[i] !== '' && derived[i] != null) {
        head[i] = derived[i]
        sources[i] = derivedSource(i)
      }
    })
  }
  // Rows 1..N are server values verbatim — never recomputed. Only row 0 has been
  // validated against the SUM-only guardrail, so re-adding these would risk
  // inventing a total (e.g. a YTD line is not the sum of the PTD rows above it).
  return { rows: [head, ...published.slice(1)], sources }
}

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export const exportClientCsv = (view, scope, filename = 'report-view.csv', meta = {}) => {
  // CC-50029: the client CSV used to be unconditionally "pure data" while the
  // client Excel stamped title/filters/generated rows — so the SAME run produced
  // a timestamped .xlsx and an untimestamped .csv (QA caught it on Trial
  // Balance). Exports of financials must say when and over what period they
  // were produced, so the CSV now takes the same meta block, written as
  // single-cell rows above the header — the exact shape cb-api already prepends
  // to the server CSV streams. "Data only" passes an empty meta and keeps the
  // pure-data file for system imports.
  // Numeric cells serialize as bare numbers, so Excel/Sheets parse them as numbers.
  const metaRows = [meta.title, meta.subtitle, meta.filtersLine, meta.generatedLine]
    .filter(Boolean)
    .map((line) => [line])
  const { headers, matrix, cols } = buildExportMatrix(view, scope)
  // CC-49457 totals on filtered exports; CC-49860 carries the WHOLE footer block,
  // so a two-line footer exports as two lines.
  const footerRows = buildFooterRows(cols, matrix, view)
  // CC-49337: currency columns serialize as "$1,234.56" strings — Excel and
  // Sheets both parse $-prefixed values as currency-formatted NUMBERS, so the
  // export stays summable while looking like the report.
  // Spec 212: a column whose view says `grouping: false` ("Show commas in numbers"
  // off) writes "$1234.56", as the screen shows it.
  const fmtCurrency = (v, decimals = 2, grouping = true) =>
    typeof v === 'number'
      ? `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: grouping })}`
      : v
  // Spec 133: a CSV is line-oriented — a two-line cell ("Kayveo" over "82", AP
  // Detail's Vendor / Bill) is written on ONE line, "Kayveo 82", so a system
  // import reading one record per line never splits a row. The PDF and XLSX keep
  // the line break, as the screen does.
  // CC-53905: a currency column is written at the view's decimals, which are now
  // the column's DISPLAYED precision, and a 0 is honoured (it used to read as
  // "falsy, use 2"). So a C0 column writes CB's declared precision, "$1,541,996"
  // and "$1,560" — what the screen shows, and what CB's own server CSV writes (it
  // writes each cell's display). Under Show $ ON this file used to write the
  // dataValue's cents ("$1,541,996.25", "$1,559.85"): both export views forced 2
  // decimals. The digits come from rounding the dataValue here, in JS; spec 155
  // §3(c) says why that equals CB's own C0 rounding. Show $ OFF writes the raw
  // dataValue (1541996.25), and so does auto unless CB's display itself carries
  // a "$".
  const applyCurrency = (row) => row.map((v, i) => singleLine(cols[i]?.currency ? fmtCurrency(v, Number.isFinite(cols[i].decimals) ? cols[i].decimals : 2, cols[i].grouping !== false) : v))
  const body = matrix.map(applyCurrency)
  const totalRows = footerRows.map(applyCurrency)
  const csv = unparse([...metaRows, headers, ...body, ...totalRows])
  triggerDownload(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename)
}

/**
 * Styled .xlsx via ExcelJS. Mirrors the server export: a bigger/bold report
 * title + description rows (merged across the table), a blank row, a bold +
 * highlighted header row, then data — with auto-fit-ish column widths. Numeric
 * columns are written as REAL numbers so totals are summable. Async (ExcelJS
 * writes a buffer); callers must await.
 */
// Build the ExcelJS workbook for a view (exported separately so tests can
// inspect REAL output — cell values, number formats, header rows — instead of
// trusting the wiring).
export const buildExcelWorkbook = async (view, scope, meta = {}) => {
  const { default: ExcelJS } = await import('exceljs')
  const { headers, matrix, cols } = buildExportMatrix(view, scope)
  // CC-49457 totals on filtered exports; CC-49860 carries the WHOLE footer block.
  const footerRows = buildFooterRows(cols, matrix, view)
  const lastCol = Math.max(headers.length, 1)
  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('Report')

  let r = 1
  // Bigger/bold title, muted italic subtitle — each merged across the table width.
  if (meta.title) {
    ws.mergeCells(r, 1, r, lastCol)
    const cell = ws.getCell(r, 1)
    cell.value = meta.title
    cell.font = { bold: true, size: 16 }
    r += 1
  }
  if (meta.subtitle) {
    ws.mergeCells(r, 1, r, lastCol)
    const cell = ws.getCell(r, 1)
    cell.value = meta.subtitle
    cell.font = { italic: true, size: 11, color: { argb: 'FF6B7280' } }
    r += 1
  }
  // CC-49325/49326: applied criteria (date range + basis) under the title, so
  // an exported file says what period/filters its numbers reflect.
  if (meta.filtersLine) {
    ws.mergeCells(r, 1, r, lastCol)
    const cell = ws.getCell(r, 1)
    cell.value = meta.filtersLine
    cell.font = { italic: true, size: 10, color: { argb: 'FF6B7280' } }
    r += 1
  }
  // CC-49340: run timestamp under the filters line — exported financials say
  // exactly when and over what period they were produced.
  if (meta.generatedLine) {
    ws.mergeCells(r, 1, r, lastCol)
    const cell = ws.getCell(r, 1)
    cell.value = meta.generatedLine
    cell.font = { italic: true, size: 10, color: { argb: 'FF6B7280' } }
    r += 1
  }
  if (meta.title || meta.subtitle || meta.filtersLine || meta.generatedLine) r += 1 // blank spacer row

  // Bold + highlighted header row.
  const headerRow = ws.getRow(r)
  headers.forEach((h, i) => {
    const cell = headerRow.getCell(i + 1)
    cell.value = h
    cell.font = { bold: true }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } }
    cell.alignment = { vertical: 'middle' }
  })
  r += 1

  // Data rows — numeric columns land as true numeric cells.
  for (const rowVals of matrix) {
    const row = ws.getRow(r)
    rowVals.forEach((v, i) => {
      const cell = row.getCell(i + 1)
      cell.value = v == null ? '' : v
      // Spec 133: a two-line cell (AP Detail's Vendor / Bill) wraps onto its two
      // lines, as the screen shows it, instead of Excel printing one long line.
      if (typeof v === 'string' && v.includes('\n')) cell.alignment = { wrapText: true, vertical: 'top' }
    })
    r += 1
  }

  // Bold footer rows with a top rule (CC-49457). Summed numeric cells stay numeric.
  // CC-49860: the whole block, not just the first row — the rule goes on the first
  // row only so a multi-line footer reads as one block.
  footerRows.forEach((totals, fi) => {
    const row = ws.getRow(r)
    totals.forEach((v, i) => {
      const cell = row.getCell(i + 1)
      cell.value = v === '' ? '' : v
      cell.font = { bold: true }
      if (fi === 0) cell.border = { top: { style: 'thin', color: { argb: 'FFD1D5DB' } } }
    })
    r += 1
  })

  // CC-49337: real Excel number formats — cells remain summable numbers but
  // display exactly like the report (currency symbol, thousands, decimals).
  cols.forEach((c, i) => {
    if (!c?.numeric) return
    const decimals = Number.isFinite(c.decimals) ? c.decimals : 2
    const places = decimals > 0 ? `.${'0'.repeat(decimals)}` : ''
    // Spec 127 §4.6: a column that DECLARES `percent` holds points (-88.6 means
    // -88.6%), so the "%" is a literal suffix — the value is unchanged, never
    // divided by 100 the way Excel's own % format would need.
    if (c.percent) {
      ws.getColumn(i + 1).numFmt = `0${places}"%"`
      return
    }
    // Spec 212: "Show commas in numbers" off (`grouping: false`) drops the
    // separator from the format only — the cell is the same number either way.
    const int = c.grouping === false ? '0' : '#,##0'
    ws.getColumn(i + 1).numFmt = c.currency
      ? `"$"${int}${places};[Red]("$"${int}${places})`
      : `${int}${places}`
  })

  // Auto-fit-ish widths from the longest cell in each column — the longest LINE of
  // a wrapped cell (spec 133), since that is what has to fit.
  const widestLine = (v) => Math.max(...String(v ?? '').split('\n').map((line) => line.length))
  headers.forEach((h, i) => {
    const longest = Math.max(String(h).length, ...matrix.map((m) => widestLine(m[i])), 8)
    ws.getColumn(i + 1).width = Math.min(longest + 2, 50)
  })

  return wb
}

export const exportClientExcel = async (view, scope, filename = 'report-view.xlsx', meta = {}) => {
  const wb = await buildExcelWorkbook(view, scope, meta)
  const buf = await wb.xlsx.writeBuffer()
  triggerDownload(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    filename,
  )
}
